// App state. Settings persist in localStorage; the current plan (takeoff, route, region) lives in
// the URL so it can be shared. Accounts can later sync the same objects.

import type { LngLat } from './geo';
import { altToM, kmhToSpeed, migrateUnits, mToAlt, speedToKmh, unitsForLocale, type Units } from './units';

export type Mode = 'reach' | 'route' | 'region';

export interface Settings {
  gr: number;
  startMode: 'ground' | 'fixed';
  /** MSL metres, used when startMode is 'fixed'. */
  startAlt: number;
  margin: number;
  launchRamp: number;
  radius: number;
  cell: number;
  /** km/h; 0 means still air. */
  windSpeed: number;
  windFrom: number;
  airspeed: number;
  units: Units;
  basemap: string;
  contours: boolean;
  airspace: boolean;
  threeD: boolean;
  // region search
  regionGr: number;
  regionMinHeight: number;
  regionSlope: boolean;
  regionAspect: boolean;
}

export const DEFAULTS: Settings = {
  gr: 6,
  startMode: 'ground',
  startAlt: 2000,
  margin: 50,
  launchRamp: 300,
  radius: 20000,
  cell: 185,
  windSpeed: 0,
  windFrom: 270,
  airspeed: 38,
  units: unitsForLocale(typeof navigator === 'undefined' ? undefined : navigator.language),
  basemap: 'terrain',
  contours: true,
  airspace: false,
  threeD: false,
  regionGr: 6,
  regionMinHeight: 305,
  regionSlope: false,
  regionAspect: false,
};

/**
 * Round stored values to tidy numbers in the given units, e.g. after switching to feet the 50 m
 * margin becomes 150 ft rather than 164 ft, and 15 km/h becomes 8 kt.
 */
export function snapToUnits(s: Settings, u: Units = s.units): void {
  const alt = (m: number, stepM: number, stepFt: number) => {
    const step = u.alt === 'ft' ? stepFt : stepM;
    return altToM(Math.round(mToAlt(m, u) / step) * step, u);
  };
  s.margin = alt(s.margin, 10, 50);
  s.startAlt = alt(s.startAlt, 100, 500);
  s.launchRamp = alt(s.launchRamp, 50, 100);
  s.regionMinHeight = alt(s.regionMinHeight, 50, 100);
  const spd = (kmh: number) => {
    const step = u.speed === 'ms' ? 0.5 : 1;
    return speedToKmh(Math.round(kmhToSpeed(kmh, u) / step) * step, u);
  };
  s.windSpeed = spd(s.windSpeed);
  s.airspeed = spd(s.airspeed);
}
snapToUnits(DEFAULTS);

const KEY = 'glide-planner:settings:v1';

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const saved = JSON.parse(raw) as Partial<Settings> & { windOn?: boolean };
      const s = { ...DEFAULTS, ...saved, units: migrateUnits(saved.units, DEFAULTS.units) };
      if (saved.windOn === false) s.windSpeed = 0; // older versions had a separate wind toggle
      delete (s as { windOn?: boolean }).windOn;
      return s;
    }
  } catch {
    /* private mode, corrupt data */
  }
  return { ...DEFAULTS };
}

export function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

export interface Plan {
  mode: Mode;
  takeoff: LngLat | null;
  route: LngLat[];
  /** Glide ratio override for the drawn route (set by region search hand-off). */
  routeGr: number | null;
  area: [LngLat, LngLat] | null;
  landings: LngLat[];
}

const EMPTY_PLAN: Plan = { mode: 'reach', takeoff: null, route: [], routeGr: null, area: null, landings: [] };

const f5 = (n: number) => n.toFixed(5).replace(/\.?0+$/, '');
const pt = (p: LngLat) => `${f5(p[1])},${f5(p[0])}`;
const parsePt = (s: string): LngLat | null => {
  const [lat, lng] = s.split(',').map(Number);
  return Number.isFinite(lat) && Number.isFinite(lng) ? [lng, lat] : null;
};
const pts = (s: string | null) => (s ? s.split(';').map(parsePt).filter((p): p is LngLat => !!p) : []);

/** URL keys for settings that matter for reproducing a result. */
const URL_SETTINGS: [keyof Settings, string][] = [
  ['gr', 'gr'],
  ['margin', 'm'],
  ['windSpeed', 'ws'],
  ['windFrom', 'wd'],
  ['airspeed', 'as'],
];

export function planToQuery(p: Plan, s: Settings): string {
  const q = new URLSearchParams();
  if (p.mode !== 'reach') q.set('mode', p.mode);
  if (p.takeoff) q.set('t', pt(p.takeoff));
  if (p.route.length) q.set('r', p.route.map(pt).join(';'));
  if (p.routeGr !== null) q.set('rgr', String(p.routeGr));
  if (p.area) q.set('a', p.area.map(pt).join(';'));
  if (p.landings.length) q.set('l', p.landings.map(pt).join(';'));
  for (const [k, short] of URL_SETTINGS) if (s[k] !== DEFAULTS[k]) q.set(short, String(s[k]));
  q.set('s', s.startMode === 'ground' ? 'g' : String(Math.round(s.startAlt)));
  return q.toString();
}

export function queryToPlan(query: string, base: Settings): { plan: Plan; settings: Settings } {
  const q = new URLSearchParams(query);
  const plan: Plan = { ...EMPTY_PLAN, route: [], landings: [] };
  const m = q.get('mode');
  if (m === 'route' || m === 'region') plan.mode = m;
  plan.takeoff = q.get('t') ? parsePt(q.get('t')!) : null;
  plan.route = pts(q.get('r'));
  plan.routeGr = q.get('rgr') ? Number(q.get('rgr')) : null;
  const a = pts(q.get('a'));
  plan.area = a.length === 2 ? [a[0], a[1]] : null;
  plan.landings = pts(q.get('l'));
  const settings = { ...base };
  // A shared plan carries every non-default value, so a missing key means the default.
  const shared = q.has('t') || q.has('r') || q.has('a');
  for (const [k, short] of URL_SETTINGS) {
    const v = q.get(short);
    if (v !== null && Number.isFinite(Number(v))) (settings[k] as number) = Number(v);
    else if (shared) (settings[k] as number) = DEFAULTS[k] as number;
  }
  const s = q.get('s');
  if (s === 'g') settings.startMode = 'ground';
  else if (s && Number.isFinite(Number(s))) {
    settings.startMode = 'fixed';
    settings.startAlt = Number(s);
  }
  return { plan, settings };
}

const initial = queryToPlan(typeof location === 'undefined' ? '' : location.search, typeof localStorage === 'undefined' ? { ...DEFAULTS } : loadSettings());

export const settings: Settings = $state(initial.settings);

// ---- small per-device flags ------------------------------------------------------------------
const VIEW_KEY = 'glide-planner:view:v1';
const WELCOME_KEY = 'glide-planner:welcomed:v1';

function get(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function set(key: string, v: string): void {
  try {
    localStorage.setItem(key, v);
  } catch {
    /* ignore */
  }
}

export function loadView(): { center: LngLat; zoom: number } | null {
  try {
    const v = JSON.parse(get(VIEW_KEY) ?? 'null');
    return v && Array.isArray(v.center) && Number.isFinite(v.zoom) ? v : null;
  } catch {
    return null;
  }
}
export const saveView = (center: LngLat, zoom: number) => set(VIEW_KEY, JSON.stringify({ center, zoom }));
export const welcomed = () => get(WELCOME_KEY) === '1';
export const markWelcomed = () => set(WELCOME_KEY, '1');
export const plan: Plan = $state(initial.plan);
